import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { tmApi, type TmProjectView, type TmTaskPriority, type TmUserOption } from "../../api/tm";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import { UserCombobox } from "../../components/tm/UserCombobox";
import { ProjectCombobox } from "../../components/tm/ProjectCombobox";
import { LocalAttachmentStager } from "../../components/shared/LocalAttachmentStager";
import { RichTextEditor } from "../../components/tm/RichTextEditor";
import type { ApiError } from "../../api/client";

const TM_ALLOWED_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/zip",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain", "text/csv",
];
const TM_MAX_BYTES = 25 * 1024 * 1024;
const TM_MAX_FILES = 10;

export default function TmTaskNewPage() {
  const { canWrite } = useSectionAccess("tm_tasks");
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const parentTaskId = params.get("parentTaskId");
  // When the task is created from a project's "Add task" CTA the project is
  // pre-selected and the picker is locked so the task can't accidentally
  // escape that project.
  const lockedProjectId = params.get("projectId");

  const [users, setUsers] = useState<TmUserOption[]>([]);
  const [projects, setProjects] = useState<TmProjectView[]>([]);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TmTaskPriority>("MEDIUM");
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(null);
  const [validatorUserId, setValidatorUserId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(lockedProjectId);
  const [dueDate, setDueDate] = useState<string>("");
  const [parentTaskTitle, setParentTaskTitle] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    void tmApi.users().then(setUsers).catch(() => undefined);
    void tmApi.listProjects().then(setProjects).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!parentTaskId) return;
    void tmApi.get(parentTaskId).then((p) => {
      setParentTaskTitle(p.title);
      // Inherit the project from the parent so subtasks roll up cleanly.
      if (p.projectId) setProjectId(p.projectId);
    }).catch(() => undefined);
  }, [parentTaskId]);

  async function uploadStaged(taskId: string) {
    for (const f of files) {
      const presigned = await tmApi.presignAttachment(taskId, {
        fileName: f.name,
        contentType: f.type,
        sizeBytes: f.size,
      });
      const res = await fetch(presigned.url, {
        method: "PUT",
        headers: { "Content-Type": f.type },
        body: f,
      });
      if (!res.ok) throw new Error(`Upload failed for ${f.name}: ${res.status}`);
      await tmApi.confirmAttachment(taskId, {
        objectKey: presigned.objectKey,
        fileName: f.name,
        contentType: f.type,
        sizeBytes: f.size,
      });
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    if (!assigneeUserId) {
      toast.error(t("tm.tasks.new.errAssigneeRequired", "Pick an assignee"));
      return;
    }
    setBusy(true);
    try {
      const created = await tmApi.create({
        title,
        description: description || null,
        priority,
        assigneeUserId,
        validatorUserId,
        projectId: projectId || null,
        dueDate: dueDate || null,
        parentTaskId: parentTaskId ?? null,
      });
      if (files.length > 0) {
        try {
          await uploadStaged(created.id);
        } catch (_err) {
          toast.error(t("tm.tasks.new.attachFailed",
            { defaultValue: "Task created but some attachments failed. Retry from the detail page." }));
          navigate(`/tm/tasks/${created.id}`, { replace: true });
          return;
        }
      }
      toast.success(t("tm.tasks.new.toastCreated", "Task created."));
      navigate(`/tm/tasks/${created.id}`, { replace: true });
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ maxWidth: 760, margin: "0 auto" }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)" }}>
          {parentTaskTitle
            ? t("tm.tasks.new.titleSubtask", { defaultValue: `New subtask of "${parentTaskTitle}"`, parent: parentTaskTitle })
            : t("tm.tasks.new.title", "New task")}
        </h1>
      </div>
      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <label className="field">
            <span>{t("tm.tasks.new.titleLabel", "Title")} *</span>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
                   required maxLength={300}
                   placeholder={t("tm.tasks.new.titlePlaceholder", "What needs to be done?")} />
          </label>

          <label className="field">
            <span>{t("tm.tasks.new.description", "Description")}</span>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder={t("tm.tasks.new.descriptionPlaceholder", "Add detail, context, links…")}
            />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label className="field">
              <span>{t("tm.tasks.new.assignee", "Assignee")} *</span>
              <UserCombobox users={users} value={assigneeUserId} onChange={setAssigneeUserId} />
            </label>
            <label className="field">
              <span>{t("tm.tasks.new.validator", "Validator (optional)")}</span>
              <UserCombobox users={users} value={validatorUserId} onChange={setValidatorUserId} />
            </label>
            <label className="field">
              <span>{t("tm.tasks.new.priority", "Priority")}</span>
              <Select<TmTaskPriority>
                value={priority}
                onChange={setPriority}
                ariaLabel={t("tm.tasks.new.priority", "Priority")}
                options={[
                  { value: "LOW", label: t("tm.priority.LOW", "Low") },
                  { value: "MEDIUM", label: t("tm.priority.MEDIUM", "Medium") },
                  { value: "HIGH", label: t("tm.priority.HIGH", "High") },
                  { value: "URGENT", label: t("tm.priority.URGENT", "Urgent") },
                ]}
              />
            </label>
            <label className="field">
              <span>{t("tm.tasks.new.dueDate", "Due date")}</span>
              <input type="date" className="input" value={dueDate}
                     onChange={(e) => setDueDate(e.target.value)} />
            </label>
            <label className="field" style={{ gridColumn: "1 / -1" }}>
              <span>{t("tm.tasks.new.project", "Project (optional)")}</span>
              <ProjectCombobox projects={projects} value={projectId} onChange={setProjectId} disabled={!!lockedProjectId} />
            </label>
          </div>

          <div className="field">
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink-3)",
                           letterSpacing: ".02em", textTransform: "uppercase" }}>
              {t("tm.tasks.new.attachments", "Attachments")}
            </span>
            <LocalAttachmentStager
              files={files}
              onChange={setFiles}
              disabled={busy || !canWrite}
              allowedTypes={TM_ALLOWED_TYPES}
              maxBytes={TM_MAX_BYTES}
              maxFiles={TM_MAX_FILES}
              i18nPrefix="tm.attachments."
              fileTypesHint={t("tm.tasks.new.attachmentsHint",
                `Images, PDF, Office docs, ZIP · max 25 MB · up to ${TM_MAX_FILES} files`)}
            />
          </div>
        </div>
        <div style={{
          display: "flex", gap: 8, justifyContent: "flex-end",
          padding: "14px 18px", borderTop: "1px solid var(--border)",
          background: "var(--surface-2)", borderRadius: "0 0 var(--r-md) var(--r-md)",
        }}>
          <button type="button" className="btn" onClick={() => navigate(-1)}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn primary" disabled={busy || !canWrite}>
            {busy ? t("tm.tasks.new.creating", "Creating…") : t("tm.tasks.new.create", "Create task")}
          </button>
        </div>
      </div>
    </form>
  );
}
